import { slicedToArray as _slicedToArray, createForOfIteratorHelper as _createForOfIteratorHelper, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import PropTypes from 'prop-types';
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useLocalStorage } from '../../hooks/useLocalStorage.js';
import { getMenuData } from '../../utils/menuHelper.js';
import { noopHook } from '../../utils/noop.js';
import TopRightMenu from '../Headers/TopRightMenu.js';
import { isObjectWithNoProperties, createLearnerHeader, createDefaultHeader } from './utils.js';
import TopLeftMenu from '../Headers/TopLeftMenu.js';
import { HeaderMenu } from '../HeaderMenu.js';
import { contentUserNavigation } from '../../utils/menus/content_user_navigation_menu.js';
import { i18n } from '../../i18n/translate.js';
import { FAIcon } from '../fa-icon/component.js';
function HeaderMenuWrapper(_ref) {
  var _ref$appendLanguage = _ref.appendLanguage,
    appendLanguage = _ref$appendLanguage === void 0 ? true : _ref$appendLanguage,
    authCapabilities = _ref.authCapabilities,
    authUrls = _ref.authUrls,
    content = _ref.content,
    currentUrl = _ref.currentUrl,
    customLeft = _ref.customLeft,
    customRight = _ref.customRight,
    environment = _ref.environment,
    helpMessage = _ref.helpMessage,
    helpMessageEnabled = _ref.helpMessageEnabled,
    _ref$hideImpersonatio = _ref.hideImpersonation,
    hideImpersonation = _ref$hideImpersonatio === void 0 ? false : _ref$hideImpersonatio,
    hideLogin = _ref.hideLogin,
    idProvidersUrl = _ref.idProvidersUrl,
    _ref$isDashiFi = _ref.isDashiFi,
    isDashiFi = _ref$isDashiFi === void 0 ? false : _ref$isDashiFi,
    _ref$isFoundry = _ref.isFoundry,
    isFoundry = _ref$isFoundry === void 0 ? false : _ref$isFoundry,
    _ref$isHomeroom = _ref.isHomeroom,
    isHomeroom = _ref$isHomeroom === void 0 ? false : _ref$isHomeroom,
    _ref$isMobile = _ref.isMobile,
    isMobile = _ref$isMobile === void 0 ? false : _ref$isMobile,
    isUnauthenticated = _ref.isUnauthenticated,
    languages = _ref.languages,
    loginText = _ref.loginText,
    logoOverrideHyperlink = _ref.logoOverrideHyperlink,
    logoOverrideUrl = _ref.logoOverrideUrl,
    onExitImpersonation = _ref.onExitImpersonation,
    onLoginLoad = _ref.onLoginLoad,
    organization = _ref.organization,
    program = _ref.program,
    selectedLanguage = _ref.selectedLanguage,
    service = _ref.service,
    slug = _ref.slug,
    socialIcon = _ref.socialIcon,
    theme = _ref.theme,
    user = _ref.user,
    _ref$useSearchParams = _ref.useSearchParams,
    useSearchParams = _ref$useSearchParams === void 0 ? noopHook : _ref$useSearchParams;
  var isEverfiAdmin = !organization && !isHomeroom;
  var isNextAdmin = isEverfiAdmin && service === 'next';
  var isDeactivatedOrg = !isUnauthenticated && (organization === null || organization === void 0 ? void 0 : organization.active) === false;
  var hideMenu = isUnauthenticated && isEverfiAdmin;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isMobileMenuOpen = _useState2[0],
    setMobileMenuOpen = _useState2[1];
  var _useState3 = useState(isObjectWithNoProperties(program) || (program === null || program === void 0 ? void 0 : program.primary_color) === null),
    _useState4 = _slicedToArray(_useState3, 2),
    hasColors = _useState4[0],
    setHasColors = _useState4[1];
  var navbarRef = useRef(null);
  var _useSearchParams = useSearchParams(),
    _useSearchParams2 = _slicedToArray(_useSearchParams, 1),
    searchParams = _useSearchParams2[0];
  var currentRuleSetName = null;
  var isImpersonating = !!(user !== null && user !== void 0 && user.impersonator_id);
  var isCustom = customLeft || customRight;
  if (searchParams) {
    currentRuleSetName = searchParams.get('current_rule_set_name');
  }
  var buildMenuData = useCallback(function () {
    if (slug) {
      return getMenuData({
        slug: slug,
        environment: environment,
        user: user,
        organization: organization,
        currentRuleSetName: currentRuleSetName,
        isAdmin: isEverfiAdmin,
        locale: selectedLanguage,
        currentUrl: currentUrl
      });
    }
  }, [slug, environment, user, organization, currentRuleSetName, isEverfiAdmin, selectedLanguage, currentUrl]);
  var _useLocalStorage = useLocalStorage("".concat(slug, "-data"), buildMenuData()),
    _useLocalStorage2 = _slicedToArray(_useLocalStorage, 2),
    menuData = _useLocalStorage2[0],
    setMenuData = _useLocalStorage2[1];
  var observer;
  // Create an instance of MutationObserver
  if (typeof window !== 'undefined') {
    var MutationObserver = window.MutationObserver || window.WebKitMutationObserver || window.MozMutationObserver;
    observer = new MutationObserver(function (mutations) {
      return handleMutations(mutations);
    });

    /**
     * We are watching for Next to inject customer colors into
     * the head of the document. We have to wait to display some
     * header elements until this happens.
     */
    // Start observing the target node for configured mutations
    var targetNode = document.head;
    observer.observe(targetNode, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Removes DOM mutation event listener
   */
  var removeStyleTagEventListener = function removeStyleTagEventListener() {
    if (observer) {
      observer.disconnect();
    }
  };

  /**
   * Handler for Next DOM injection
   * Sets hasColors = true if injected style element detected
   * @param {*} mutations
   */
  var handleStyleTagInjection = function handleStyleTagInjection(mutations) {
    var _mutations$addedNodes;
    // this is the element injected by Next
    var styleTag = (mutations === null || mutations === void 0 || (_mutations$addedNodes = mutations.addedNodes) === null || _mutations$addedNodes === void 0 || (_mutations$addedNodes = _mutations$addedNodes[0]) === null || _mutations$addedNodes === void 0 || (_mutations$addedNodes = _mutations$addedNodes.id) === null || _mutations$addedNodes === void 0 ? void 0 : _mutations$addedNodes.includes('custom-colors')) !== -1;

    // injected element detected
    if (styleTag) {
      removeStyleTagEventListener();
      // there is a CSS transition of 0.15 seconds
      // this delays displaying the buttons until
      // transition has completed
      setTimeout(function () {
        setHasColors(true);
      }, 250);
    }
  };
  var handleMutations = function handleMutations(mutationsList) {
    var _iterator = _createForOfIteratorHelper(mutationsList),
      _step;
    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var mutation = _step.value;
        if (mutation.type === 'childList' || mutation.type === 'subtree') {
          handleStyleTagInjection(mutation);
        }
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
  };
  useEffect(function () {
    // if primary_color is null, then we use default colors
    if (!hasColors && (isObjectWithNoProperties(program) || (program === null || program === void 0 ? void 0 : program.primary_color) === null)) {
      setHasColors(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(function () {
    setMenuData(buildMenuData());
  }, [currentRuleSetName, organization, setMenuData, slug, user, buildMenuData]);
  useEffect(function () {
    if (isDeactivatedOrg && !document.body.classList.contains('deactivated')) {
      document.body.classList.add('deactivated');
    }
  }, []);
  var getFoundryClass = function getFoundryClass() {
    return typeof document !== 'undefined' && document.body.classList.contains('foundry') ? '' : 'foundry';
  };
  var getRoleListForUi = function getRoleListForUi() {
    var roleList = [];
    user.rule_sets.map(function (rs) {
      if (rs.roles) {
        rs.roles.forEach(function (role) {
          if (role.slug === user.rule_set_roles[rs.id]) {
            roleList.push("".concat(rs.label, " : ").concat(rs.role || ''));
          }
        });
      } else {
        roleList.push("".concat(rs.label, " : ").concat(rs.role));
      }
    });
    return roleList.join(', ');
  };
  var isLearner = function isLearner() {
    var learner = false;
    if (user !== null && user !== void 0 && user.user_rule_set_roles) {
      Object.values(user.user_rule_set_roles).forEach(function (value) {
        learner = value.role === 'learner';
      });
    }
    return learner || (user === null || user === void 0 ? void 0 : user.guest);
  };
  var isK12User = function isK12User() {
    var _user$user_rule_set_r;
    return (user === null || user === void 0 || (_user$user_rule_set_r = user.user_rule_set_roles) === null || _user$user_rule_set_r === void 0 ? void 0 : _user$user_rule_set_r.find(function (_ref2) {
      var role = _ref2.role;
      return role === 'k12';
    })) && isHomeroom;
  };
  var handleBeforeExitImpersonation = function handleBeforeExitImpersonation() {
    localStorage.removeItem('current_rule_set_name');
    if (onExitImpersonation) {
      onExitImpersonation();
    }
  };
  var renderImpersonationBanner = function renderImpersonationBanner() {
    var config = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    if (hideImpersonation) return null;
    var _config$className = config.className,
      className = _config$className === void 0 ? '' : _config$className;
    var organizationName = (organization === null || organization === void 0 ? void 0 : organization.name) || (user === null || user === void 0 ? void 0 : user.organization_name);
    var exitImpersonationUrl = user !== null && user !== void 0 && user.homeroom_user ? "".concat(environment.HOMEROOM_ADMIN_URL, "/admin/impersonate/destroy") : "".concat(environment.MENU_DOMAIN_FOR_ADMINIFI, "/admin/impersonation/sign_out");
    var showExitImpersonation = !!(environment.MENU_DOMAIN_FOR_ADMINIFI || environment.MENU_DOMAIN_FOR_HOMEROOM);
    return /*#__PURE__*/React.createElement("div", {
      className: "impersonating-header p3 ".concat(className),
      "data-testid": "impersonating-header"
    }, /*#__PURE__*/React.createElement("div", {
      className: "impersonation-label bold"
    }, /*#__PURE__*/React.createElement(FAIcon, {
      icon: "mask"
    }), "Impersonate Mode"), organizationName && /*#__PURE__*/React.createElement("div", {
      className: "impersonation-org"
    }, " Organization: ", organizationName, " "), user && !isK12User() && /*#__PURE__*/React.createElement("div", {
      className: "impersonation-roles"
    }, getRoleListForUi()), showExitImpersonation && /*#__PURE__*/React.createElement("div", {
      className: "impersonation-exit ui-compact"
    }, /*#__PURE__*/React.createElement("a", {
      onClick: handleBeforeExitImpersonation,
      href: exitImpersonationUrl
    }, /*#__PURE__*/React.createElement(FAIcon, {
      icon: "power-off"
    }), " Exit")));
  };
  var renderDeactivatedOrgBanner = function renderDeactivatedOrgBanner() {
    return /*#__PURE__*/React.createElement("div", {
      className: 'deactivated-organization-header p3',
      "data-testid": "deactivated-organization-header"
    }, /*#__PURE__*/React.createElement("div", {
      className: "deactivated-organization-label"
    }, /*#__PURE__*/React.createElement(FAIcon, {
      icon: "triangle-exclamation"
    }), i18n.t('Account Deactivated')), /*#__PURE__*/React.createElement("div", {
      className: "deactivated-organization-desc"
    }, user !== null && user !== void 0 && user.is_manager ? i18n.t('Please reach out to your customer support representative for assistance.') : i18n.t('Please reach out to your program administrator.')));
  };
  var rightMenu = function rightMenu() {
    var extras = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return /*#__PURE__*/React.createElement(TopRightMenu, _extends({
      adminifiDomain: environment.MENU_DOMAIN_FOR_ADMINIFI,
      homeroomDomain: environment.MENU_DOMAIN_FOR_HOMEROOM,
      authCapabilities: authCapabilities,
      authUrls: authUrls,
      content: content,
      customRight: customRight,
      helpMessage: helpMessage,
      helpMessageEnabled: helpMessageEnabled,
      hideLogin: hideLogin,
      hideMenu: hideMenu,
      idProvidersUrl: idProvidersUrl,
      isFoundry: isFoundry,
      isHomeroom: isHomeroom,
      isImpersonating: isImpersonating,
      isMobile: isMobile,
      languages: languages,
      loginText: loginText,
      menuData: menuData,
      onLoginLoad: onLoginLoad,
      onStopImpersonation: handleBeforeExitImpersonation,
      selectedLanguage: selectedLanguage,
      socialIcon: socialIcon,
      user: user
    }, extras));
  };
  var leftMenu = function leftMenu() {
    var extras = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return /*#__PURE__*/React.createElement(TopLeftMenu, _extends({
      isDashiFi: isDashiFi,
      isMobile: isMobile,
      isImpersonating: isImpersonating,
      customLeft: customLeft,
      isNextAdmin: isNextAdmin,
      isEverfiAdmin: isEverfiAdmin,
      isUnauthenticated: isUnauthenticated,
      logoOverrideHyperlink: logoOverrideHyperlink,
      logoOverrideUrl: logoOverrideUrl,
      organization: organization
    }, extras));
  };
  if (!menuData) return /*#__PURE__*/React.createElement("header", {
    id: "header"
  });
  var getHeaderMenu = function getHeaderMenu() {
    var _user$rule_sets;
    return !!contentUserNavigation && !hideMenu && !!content && /*#__PURE__*/React.createElement(HeaderMenu, {
      additionalNavClass: "content-nav",
      isImpersonating: isImpersonating,
      isMobile: isMobile,
      menuData: contentUserNavigation,
      currentRuleSets: (_user$rule_sets = user === null || user === void 0 ? void 0 : user.rule_sets) !== null && _user$rule_sets !== void 0 ? _user$rule_sets : [],
      helpMessage: helpMessage,
      helpMessageEnabled: helpMessageEnabled,
      organizationSlug: user === null || user === void 0 ? void 0 : user.organization_slug,
      user: user,
      adminifiDomain: environment.MENU_DOMAIN_FOR_ADMINIFI,
      languages: languages,
      selectedLanguage: selectedLanguage,
      appendLanguage: appendLanguage
    });
  };
  var config = {
    content: content,
    currentUrl: currentUrl,
    customRight: customRight,
    getFoundryClass: getFoundryClass,
    hasColors: hasColors,
    isCustom: isCustom,
    isDashiFi: isDashiFi,
    isImpersonating: isImpersonating,
    isLearner: isLearner,
    isMobile: isMobile,
    isMobileMenuOpen: isMobileMenuOpen,
    isNextAdmin: isNextAdmin,
    isDeactivatedOrg: isDeactivatedOrg,
    leftMenu: leftMenu,
    menu: getHeaderMenu(),
    navbarRef: navbarRef,
    program: program,
    renderImpersonationBanner: renderImpersonationBanner,
    renderDeactivatedOrgBanner: renderDeactivatedOrgBanner,
    rightMenu: rightMenu,
    setMobileMenuOpen: setMobileMenuOpen,
    theme: theme
  };
  if (slug.includes('learner')) {
    return createLearnerHeader(config);
  }
  return createDefaultHeader(config);
}
HeaderMenuWrapper.displayName = 'HeaderMenuWrapper';
HeaderMenuWrapper.propTypes = {
  appendLanguage: PropTypes.bool,
  authCapabilities: PropTypes.object,
  authUrls: PropTypes.object,
  content: PropTypes.object,
  currentUrl: PropTypes.string,
  customLeft: PropTypes.string,
  customRight: PropTypes.string,
  environment: PropTypes.object,
  helpMessage: PropTypes.string,
  helpMessageEnabled: PropTypes.bool,
  hideImpersonation: PropTypes.bool,
  hideLogin: PropTypes.bool,
  idProvidersUrl: PropTypes.string,
  isDashiFi: PropTypes.bool,
  isFoundry: PropTypes.bool,
  isHomeroom: PropTypes.bool,
  isMobile: PropTypes.bool,
  isUnauthenticated: PropTypes.bool,
  languages: PropTypes.object,
  loginText: PropTypes.string,
  logoOverrideHyperlink: PropTypes.string,
  logoOverrideUrl: PropTypes.string,
  onExitImpersonation: PropTypes.func,
  onLoginLoad: PropTypes.func,
  organization: PropTypes.object,
  program: PropTypes.object,
  selectedLanguage: PropTypes.string,
  service: PropTypes.string,
  slug: PropTypes.string,
  socialIcon: PropTypes.string,
  theme: PropTypes.object,
  user: PropTypes.object,
  useSearchParams: PropTypes.func,
  UtilityFunctions: PropTypes.object
};
export { HeaderMenuWrapper };